import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { toastEdgeFunctionError } from '@/utils/toastEdgeFunction';

export interface AICreativeModel {
  id: string;
  brand_id: string;
  provider: string;
  model_id: string;
  model_name: string;
  description?: string;
  supported_types: string[];
  enabled: boolean;
  group?: string;
  created_at: string;
  updated_at: string;
}

export const useAICreativeModels = (brandId: string) => {
  const queryClient = useQueryClient();

  // Fetch AI creative models for a brand
  const fetchModels = async (): Promise<AICreativeModel[]> => {
    if (!brandId) return [];
    
    const { data, error } = await supabase
      .from('cm_ai_creative_models')
      .select('*')
      .eq('brand_id', brandId)
      .order('provider', { ascending: true })
      .order('model_name', { ascending: true });

    if (error) throw error;
    return (data || []).map(item => ({
      ...item,
      supported_types: Array.isArray(item.supported_types) 
        ? item.supported_types.filter((type): type is string => typeof type === 'string')
        : []
    }));
  };

  const modelsQuery = useQuery({
    queryKey: ['ai-creative-models', brandId],
    queryFn: fetchModels,
    enabled: !!brandId,
  });

  // Fetch models from AI provider APIs
  const fetchProviderModels = useMutation({
    mutationFn: async ({ provider }: { provider: string }) => {
      const { data, error } = await supabase.functions.invoke('fetch-ai-models', {
        body: { provider, brandId }
      });

      if (error) throw error;
      return data.models;
    },
    onSuccess: () => {
      toast.success('Models fetched successfully');
    },
    onError: (error: any) => {
      console.error('Error fetching provider models:', error);
      toastEdgeFunctionError('fetch-ai-models', error, 'Failed to fetch models');
    }
  });

  // Save models to database
  const saveModels = useMutation({
    mutationFn: async (models: Omit<AICreativeModel, 'id' | 'created_at' | 'updated_at'>[]) => {
      const { data, error } = await supabase
        .from('cm_ai_creative_models')
        .upsert(
          models.map(model => ({
            ...model,
            brand_id: brandId
          })),
          {
            onConflict: 'brand_id,provider,model_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-creative-models', brandId] });
      toast.success('Models saved successfully');
    },
    onError: (error: any) => {
      console.error('Error saving models:', error);
      toast.error(`Failed to save models: ${error.message}`);
    }
  });

  // Update model configuration
  const updateModel = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AICreativeModel> }) => {
      const { data, error } = await supabase
        .from('cm_ai_creative_models')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-creative-models', brandId] });
      toast.success('Model updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating model:', error);
      toast.error(`Failed to update model: ${error.message}`);
    }
  });

  // Delete model
  const deleteModel = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cm_ai_creative_models')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-creative-models', brandId] });
      toast.success('Model deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting model:', error);
      toast.error(`Failed to delete model: ${error.message}`);
    }
  });

  return {
    models: modelsQuery.data || [],
    isLoading: modelsQuery.isLoading,
    error: modelsQuery.error,
    fetchProviderModels,
    saveModels,
    updateModel,
    deleteModel,
    refetch: modelsQuery.refetch,
  };
};